import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Menu, X, Zap, Coins, LogOut, User, Shield, Crown, Clock, LayoutDashboard, History, Settings, LayoutGrid, Video, Book, Phone } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AnnouncementBanner from "./AnnouncementBanner";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, profile, signOut } = useAuth();
  const { isAdmin } = useAdminRole();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const navLinks = [
    { href: "/pricing", label: t("ราคา", "Pricing") },
  ];

  // Generate dropdown items
  const generateItems = [
    { href: "/generate/video", label: t("สร้างวิดีโอ Veo 3", "Veo 3 Video"), icon: Video },
    { href: "/generate/photo", label: t("สร้างรูปภาพ", "Generate Image"), icon: Sparkles },
    { href: "/generate/music", label: t("สร้างเพลง", "Generate Music"), icon: Sparkles },
    { href: "/generate/text-to-speech", label: t("สร้างเสียงพูด", "Text to Speech"), icon: Sparkles },
    { href: "/generate/cover", label: t("สร้างภาพปก", "Cover Generator"), icon: Sparkles },
    // { href: "/professional", label: t("โหมดมืออาชีพ", "Pro Mode"), icon: Crown, pro: true },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isGenerateActive = location.pathname.startsWith("/generate") && !location.pathname.includes("sora");

  return (
    <>
      {/* Announcement Banner - Shows at bottom to avoid blocking navbar */}
      <div className="fixed bottom-0 left-0 right-0 z-[60]">
        <AnnouncementBanner />
      </div>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-glass border-b border-border/50 announcement-offset">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="relative">
                <Sparkles className="h-8 w-8 text-primary group-hover:scale-110 transition-transform" />
                <div className="absolute inset-0 blur-lg bg-primary/30 group-hover:bg-primary/50 transition-colors" />
              </div>
              <span className="text-xl font-heading font-bold text-gradient">
                GenCNX
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {/* สร้างสรรค์ Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 ${isGenerateActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}>
                    <Zap className="h-3.5 w-3.5" />
                    {t("สร้างสรรค์", "Generate")}
                    <svg className="h-3 w-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {generateItems.map((item) => (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link
                        to={item.href}
                        className={`cursor-pointer flex items-center gap-2 ${item.pro ? "text-amber-600 dark:text-amber-400" : ""}`}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Seedance 2.0 - Standalone */}
              <Link
                to="/generate/sora/text-to-video"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 ${isActive("/generate/sora")
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
              >
                <Video className="h-3.5 w-3.5" />
                Seedance 2.0
              </Link>

              {/* Grok Imagine - Standalone */}
              <Link
                to="/generate/grok"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 ${isActive("/generate/grok")
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Grok
              </Link>

              {/* บริการทั้งหมด */}
              <Link
                to="/all"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 ${isActive("/all")
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                {t("บริการทั้งหมด", "Services")}
              </Link>

              {/* ราคา */}
              <Link
                to="/pricing"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive("/pricing")
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
              >
                {t("ราคา", "Pricing")}
              </Link>
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center gap-2">
              <LanguageToggle />
              <ThemeToggle />

              {user ? (
                <>
                  {/* Credits Display */}
                  {/* Credits Display */}
                  <Link to="/dashboard" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer">
                    <Coins className="h-4 w-4" />
                    <span className="font-medium">{profile?.credits ?? 0}</span>
                  </Link>

                  {/* User Dropdown */}
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9">
                          <User className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>
                          {t("บัญชีของฉัน", "My Account")}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard" className="cursor-pointer flex items-center gap-2">
                            <LayoutDashboard className="h-4 w-4" />
                            {t("แดชบอร์ด", "Dashboard")}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/history" className="cursor-pointer flex items-center gap-2">
                            <History className="h-4 w-4" />
                            {t("ประวัติ", "History")}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/settings" className="cursor-pointer flex items-center gap-2">
                            <Settings className="h-4 w-4" />
                            {t("ตั้งค่า", "Settings")}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/manual" className="cursor-pointer flex items-center gap-2">
                            <Book className="h-4 w-4" />
                            {t("คู่มือ", "Manual")}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/contact" className="cursor-pointer flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            {t("ติดต่อเรา", "Contact Us")}
                          </Link>
                        </DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild>
                              <Link to="/admin" className="cursor-pointer text-primary flex items-center gap-2">
                                <Shield className="h-4 w-4" />
                                {t("แอดมิน", "Admin Panel")}
                              </Link>
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                          <LogOut className="h-4 w-4 mr-2" />
                          {t("ออกจากระบบ", "Logout")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <span className="max-w-[120px] truncate text-sm text-muted-foreground">
                      {profile?.display_name || user.email?.split("@")[0]}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      {t("เข้าสู่ระบบ", "Login")}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button variant="glow" size="sm" className="gap-2">
                      <Zap className="h-4 w-4" />
                      {t("เริ่มต้นใช้งาน", "Get Started")}
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-foreground"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-border/50 animate-fade-in max-h-[calc(100vh-80px)] overflow-y-auto">
              <div className="flex flex-col gap-2">
                {/* Generate Section */}
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("สร้างสรรค์", "Generate")}
                </div>
                {generateItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive(item.href)
                      ? "text-primary bg-primary/10"
                      : item.pro
                        ? "text-amber-600 bg-amber-500/10 hover:bg-amber-500/20 dark:text-amber-400"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                ))}

                {/* Seedance 2.0 */}
                <Link
                  to="/generate/sora/text-to-video"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/generate/sora")
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                >
                  <Video className="h-4 w-4" />
                  Seedance 2.0
                </Link>

                {/* Grok */}
                <Link
                  to="/generate/grok"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/generate/grok")
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                >
                  <Sparkles className="h-4 w-4" />
                  Grok
                </Link>

                <div className="my-2 border-t border-border/50" />

                {/* Main Nav Items */}
                <Link
                  to="/all"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/all")
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                >
                  <LayoutGrid className="h-4 w-4" />
                  {t("บริการทั้งหมด", "Services")}
                </Link>

                <Link
                  to="/pricing"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/pricing")
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                >
                  <Coins className="h-4 w-4" />
                  {t("ราคา", "Pricing")}
                </Link>

                <div className="flex items-center gap-2 px-4 py-2">
                  <LanguageToggle />
                  <ThemeToggle />
                </div>

                {user ? (
                  <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border/50">
                    {/* Credits Display */}
                    {/* Credits Display */}
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer border-b border-white/5"
                    >
                      <Coins className="h-4 w-4 text-primary" />
                      <span className="text-muted-foreground font-heading text-sm">{t("เครดิต:", "Credits:")}</span>
                      <span className="font-heading font-bold text-primary">{profile?.credits ?? 0}</span>
                    </Link>

                    <Link
                      to="/dashboard"
                      onClick={() => setIsMenuOpen(false)}
                      className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/dashboard")
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {t("แดชบอร์ด", "Dashboard")}
                    </Link>

                    <Link
                      to="/history"
                      onClick={() => setIsMenuOpen(false)}
                      className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/history")
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`}
                    >
                      <History className="h-4 w-4" />
                      {t("ประวัติ", "History")}
                    </Link>

                    <Link
                      to="/settings"
                      onClick={() => setIsMenuOpen(false)}
                      className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/settings")
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`}
                    >
                      <Settings className="h-4 w-4" />
                      {t("ตั้งค่า", "Settings")}
                    </Link>

                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setIsMenuOpen(false)}
                        className={`px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 ${isActive("/admin")
                          ? "text-primary bg-primary/10"
                          : "text-primary hover:bg-primary/5"
                          }`}
                      >
                        <Shield className="h-4 w-4" />
                        {t("แอดมิน", "Admin Panel")}
                      </Link>
                    )}

                    <button
                      className="px-4 py-3 rounded-lg font-heading font-medium transition-all flex items-center gap-2 text-destructive hover:bg-destructive/10 w-full text-left"
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                      {t("ออกจากระบบ", "Logout")}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border/50 px-2">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="ghost" className="w-full font-heading">
                        {t("เข้าสู่ระบบ", "Login")}
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="glow" className="w-full gap-2 font-heading">
                        <Zap className="h-4 w-4" />
                        {t("เริ่มต้นใช้งาน", "Get Started")}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav >
    </>
  );
};

export default Navbar;
